import { useState, type CSSProperties, type ReactElement, type ReactNode } from 'react';
import { ActionIcon, Box, Group, Text, type MantineBreakpoint } from '@mantine/core';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { type VizAccent } from '@/design/VizAccents';
import SortableColumnHeader from './SortableColumnHeader';
import RankPagination from './RankPagination';
import { useRankedSort, type RankedSortColumn, type RankedSortState } from './useRankedSort';

const RANK_WIDTH = 20;
const CHEVRON_WIDTH = 15;
const SHARE_WIDTH = 44;
const CARET_WIDTH = 16;
const INDENT_WIDTH = 20;
const ACTION_WIDTH = 32;

/** A right-aligned metric column (Prompts, Users, Conf., …). */
export interface RankedListColumn<T> {
    /** Stable identity and sort key. */
    key: string;
    /** Uppercase column header. */
    header: string;
    /** Fixed column width in px. */
    width: number;
    /** Cell content (already formatted by the caller). */
    render: (row: T) => ReactNode;
    /** Comparison for sorting; omit to make the column non-sortable. */
    sort?: RankedSortColumn<T>;
    /** Hide below this breakpoint (Mantine `visibleFrom`). */
    visibleFrom?: MantineBreakpoint;
    /** Cell font size — the headline metric reads `sm`, secondary columns `xs` (default `sm`). */
    size?: 'sm' | 'xs';
    /**
     * Render the cell content directly (no monospace right-aligned `<Text>` wrapper).
     * Use for non-text cells such as an inline sparkline SVG.
     */
    raw?: boolean;
}

/** The flexible leading column: rank, label, optional subline, and the share bar. */
export interface RankedListPrimary<T> {
    /** Stable identity and sort key. */
    key: string;
    /** Uppercase column header. */
    header: string;
    /** Primary label text (truncates). */
    label: (row: T) => ReactNode;
    /** Optional dimmed subline beneath the label (e.g. a taxonomy parent). */
    subline?: (row: T) => ReactNode;
    /** Bar fill width, 0–100 — typically the value's share of the peak row. Ignored when `hideBar`. */
    fill?: (row: T) => number;
    /** Formatted share shown beside the bar (e.g. `42%`); omit to hide the share readout. */
    share?: (row: T) => string;
    /** Drop the share bar entirely — the label stands alone (for lists whose metrics live in columns). */
    hideBar?: boolean;
    /** Comparison for sorting; omit to make the column non-sortable. */
    sort?: RankedSortColumn<T>;
}

interface RankedListProps<T> {
    rows: T[];
    /** The leading label + share-bar column. */
    primary: RankedListPrimary<T>;
    /** Trailing metric columns. */
    columns: RankedListColumn<T>[];
    /** Stable React key per row. */
    rowKey: (row: T) => string;
    /** Accent hue for the share bar. */
    accent: VizAccent;
    /** Default sort — its key must match `primary.key` or a column key. */
    initialSort: RankedSortState<string>;
    /** Row link target; rows render as real links when set. Return `undefined` for a row that shouldn't navigate (e.g. an expand-only branch). */
    hrefFor?: (row: T) => string | undefined;
    /** Click handler alternative to `hrefFor` for in-place actions. */
    onRowClick?: (row: T) => void;
    /** Plural noun for the pagination caption, e.g. `task categories`. */
    unitPlural: string;
    /** Rows per page before pagination kicks in. */
    pageSize?: number;
    /** Nested rows to render beneath a row when it is expanded. Omit for a flat list. */
    childrenFor?: (row: T) => T[] | undefined;
    /** Whether a row shows an expand caret. Defaults to "has children" when `childrenFor` is set. */
    isExpandable?: (row: T) => boolean;
    /** Per-row bar accent by nesting depth (0 = top level). Defaults to the fixed `accent`. */
    accentFor?: (row: T, depth: number) => VizAccent;
}

function headerCell(label: string, style?: CSSProperties): ReactElement {
    return (
        <Text fw={700} tt="uppercase" style={{ fontSize: '9px', letterSpacing: '0.06em', color: 'var(--bm-color-table-header-text)', ...style }}>
            {label}
        </Text>
    );
}

/**
 * The shared ranked table: a sticky header over a scrollable, paginated body of
 * rows, each led by a label with a share bar and followed by right-aligned
 * metric columns. Every column with a `sort` is click-to-sort (the whole set is
 * sorted, so rank and pagination follow). Rows link via `hrefFor` or fire
 * `onRowClick`. Callers own the columns, formatting, and sort — this owns only
 * layout, sorting, and pagination. Used by Use Cases and the Teams / Roles /
 * Power Users adoption tables.
 */
export default function RankedList<T>({ rows, primary, columns, rowKey, initialSort, hrefFor, onRowClick, unitPlural, pageSize = 25, childrenFor, isExpandable }: RankedListProps<T>): ReactElement {
    const navigable = hrefFor !== undefined || onRowClick !== undefined;

    const sortColumns: Record<string, RankedSortColumn<T>> = {};
    if (primary.sort !== undefined) sortColumns[primary.key] = primary.sort;
    for (const column of columns) if (column.sort !== undefined) sortColumns[column.key] = column.sort;

    const { sorted, state: sort, toggle, sortRows } = useRankedSort(rows, sortColumns, initialSort);

    const [page, setPage] = useState(1);
    const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
    const paginated = sorted.length > pageSize;
    const pageCount = paginated ? Math.ceil(sorted.length / pageSize) : 1;
    const safePage = Math.min(page, pageCount);
    const start = paginated ? (safePage - 1) * pageSize : 0;
    const visibleTop = paginated ? sorted.slice(start, start + pageSize) : sorted;

    const toggleExpand = (row: T): void => {
        const key = rowKey(row);
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const rowExpandable = (row: T): boolean => (isExpandable !== undefined ? isExpandable(row) : (childrenFor?.(row)?.length ?? 0) > 0);

    const rendered: { row: T; depth: number; rank: number | null }[] = [];
    let topRank = start;
    const collect = (row: T, depth: number): void => {
        rendered.push({ row, depth, rank: depth === 0 ? ++topRank : null });
        if (!expanded.has(rowKey(row))) return;
        const kids = childrenFor?.(row);
        if (kids !== undefined && kids.length > 0) for (const kid of sortRows(kids)) collect(kid, depth + 1);
    };
    for (const row of visibleTop) collect(row, 0);
    const treeAware = childrenFor !== undefined;

    return (
        <Box>
            <Box style={{ border: '1px solid var(--bm-color-border)', borderRadius: 'var(--bm-radius)', background: 'var(--bm-color-card)', boxShadow: 'var(--bm-card-shadow)', maxHeight: 'calc(100vh - 320px)', overflowY: 'auto' }}>
                <Group
                    gap="md"
                    wrap="nowrap"
                    align="center"
                    style={{
                        padding: '8px 16px',
                        borderBottom: '1px solid var(--bm-color-border)',
                        background: 'var(--bm-color-table-header)',
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                    }}
                >
                    {headerCell('#', { width: RANK_WIDTH, flexShrink: 0 })}
                    {primary.sort !== undefined ?
                        <SortableColumnHeader label={primary.header} active={sort.key === primary.key} dir={sort.dir} onSort={() => toggle(primary.key)} style={{ flex: 1, minWidth: 0 }} />
                    :   <Box style={{ flex: 1, minWidth: 0 }}>{headerCell(primary.header)}</Box>}
                    {columns.map((column) => (
                        <Box key={column.key} visibleFrom={column.visibleFrom} style={{ width: column.width, flexShrink: 0 }}>
                            {column.sort !== undefined ?
                                <SortableColumnHeader label={column.header} align="right" active={sort.key === column.key} dir={sort.dir} onSort={() => toggle(column.key)} style={{ width: '100%' }} />
                            :   headerCell(column.header, { textAlign: 'right' })}
                        </Box>
                    ))}
                {(navigable || treeAware) && <Box style={{ width: treeAware ? ACTION_WIDTH : CHEVRON_WIDTH, flexShrink: 0 }} />}
                </Group>
                {rendered.map((entry, index) => (
                    <RankedListRow
                        key={rowKey(entry.row)}
                        row={entry.row}
                        rank={entry.rank}
                        depth={entry.depth}
                        isLast={index === rendered.length - 1}
                        primary={primary}
                        columns={columns}
                        hrefFor={hrefFor}
                        onRowClick={onRowClick}
                        treeAware={treeAware}
                        expandable={rowExpandable(entry.row)}
                        expanded={expanded.has(rowKey(entry.row))}
                        onToggleExpand={toggleExpand}
                    />
                ))}
            </Box>
            {paginated && <RankPagination start={start} pageSize={pageSize} total={sorted.length} pageCount={pageCount} page={safePage} onPageChange={setPage} unitPlural={unitPlural} />}
        </Box>
    );
}

interface RankedListRowProps<T> {
    row: T;
    rank: number | null;
    depth: number;
    isLast: boolean;
    primary: RankedListPrimary<T>;
    columns: RankedListColumn<T>[];
    hrefFor?: (row: T) => string | undefined;
    onRowClick?: (row: T) => void;
    /** The list renders expand affordances (caret column + indent) for every row. */
    treeAware: boolean;
    /** This row can be expanded to reveal nested rows. */
    expandable: boolean;
    expanded: boolean;
    onToggleExpand: (row: T) => void;
}

function RankedListRow<T>({ row, rank, depth, isLast, primary, columns, hrefFor, onRowClick, treeAware, expandable, expanded, onToggleExpand }: RankedListRowProps<T>): ReactElement {
    const href = hrefFor?.(row);
    const navigable = href !== undefined || onRowClick !== undefined;
    const rowStyle: CSSProperties = {
        display: 'block',
        textDecoration: 'none',
        color: 'inherit',
        padding: '12px 16px',
        borderBottom: isLast ? undefined : '1px solid var(--bm-color-border-subtle)',
    };
    const subline = primary.subline?.(row);
    const share = primary.share?.(row);

    const isBranch = treeAware && expandable;
    const toggle = (event: { preventDefault: () => void; stopPropagation: () => void }): void => {
        event.preventDefault();
        event.stopPropagation();
        onToggleExpand(row);
    };
    const stopToggle = (event: { stopPropagation: () => void }): void => event.stopPropagation();

    const detailsButton: ReactNode =
        !isBranch ? null
        : href !== undefined ?
            <ActionIcon component={Link} to={href} onClick={stopToggle} variant="subtle" color="gray" size="sm" aria-label="View details">
                <ChevronRight size={15} />
            </ActionIcon>
        : onRowClick !== undefined ?
            <ActionIcon
                onClick={(event) => {
                    stopToggle(event);
                    onRowClick(row);
                }}
                variant="subtle"
                color="gray"
                size="sm"
                aria-label="View details"
            >
                <ChevronRight size={15} />
            </ActionIcon>
        :   null;

    const inner: ReactNode = (
        <Group gap="md" wrap="nowrap" align="center">
            <Text ff="monospace" c="dimmed" fw={700} style={{ fontSize: '11px', width: RANK_WIDTH, flexShrink: 0 }}>
                {rank ?? ''}
            </Text>
            {treeAware && (
                <>
                    {depth > 0 && <Box style={{ width: depth * INDENT_WIDTH, flexShrink: 0 }} />}
                    {expandable ?
                        <Box style={{ width: CARET_WIDTH, flexShrink: 0, display: 'flex', alignItems: 'center', color: 'var(--bm-color-text-dimmed)' }}>
                            {expanded ?
                                <ChevronDown size={15} />
                            :   <ChevronRight size={15} />}
                        </Box>
                    :   <Box style={{ width: CARET_WIDTH, flexShrink: 0 }} />}
                </>
            )}
            <Box style={{ flex: 1, minWidth: 0 }}>
                <Box mb={primary.hideBar === true ? 0 : 6} style={{ minWidth: 0 }}>
                    <Text fw={600} truncate style={{ fontSize: '15px' }}>
                        {primary.label(row)}
                    </Text>
                    {subline !== undefined && (
                        <Text c="dimmed" truncate style={{ fontSize: '11px' }}>
                            {subline}
                        </Text>
                    )}
                </Box>
                {primary.hideBar !== true && (
                    <Box style={{ position: 'relative', height: 8 }}>
                        <Box style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: share !== undefined ? SHARE_WIDTH : 0, background: 'var(--bm-color-surface)' }}>
                            <Box style={{ height: '100%', width: `${primary.fill?.(row) ?? 0}%`, background: depth > 0 ? 'var(--bm-chart-2)' : 'var(--bm-color-brand-accent)' }} />
                        </Box>
                        {share !== undefined && (
                            <Text ff="monospace" c="dimmed" style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', width: SHARE_WIDTH - 4, textAlign: 'right', fontSize: '11px', lineHeight: 1 }}>
                                {share}
                            </Text>
                        )}
                    </Box>
                )}
            </Box>
            {columns.map((column) =>
                column.raw === true ?
                    <Box key={column.key} visibleFrom={column.visibleFrom} style={{ width: column.width, flexShrink: 0 }}>
                        {column.render(row)}
                    </Box>
                :   <Text key={column.key} visibleFrom={column.visibleFrom} size={column.size ?? 'sm'} ff="monospace" fw={700} style={{ width: column.width, textAlign: 'right', flexShrink: 0 }}>
                        {column.render(row)}
                    </Text>,
            )}
            {treeAware ?
                <Box style={{ width: ACTION_WIDTH, flexShrink: 0, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    {isBranch ?
                        detailsButton
                    : navigable ?
                        <ChevronRight size={15} color="var(--bm-color-text-dimmed)" />
                    :   null}
                </Box>
            : navigable ?
                <ChevronRight size={15} color="var(--bm-color-text-dimmed)" style={{ flexShrink: 0 }} />
            :   null}
        </Group>
    );

    if (isBranch) {
        return (
            <Box
                className="topic-drill-row"
                style={{ ...rowStyle, cursor: 'pointer' }}
                onClick={(event) => toggle(event)}
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') toggle(event);
                }}
            >
                {inner}
            </Box>
        );
    }
    if (href !== undefined) {
        return (
            <Box component={Link} to={href} className="topic-drill-row" style={rowStyle}>
                {inner}
            </Box>
        );
    }
    if (onRowClick !== undefined) {
        return (
            <Box
                className="topic-drill-row"
                style={{ ...rowStyle, cursor: 'pointer' }}
                onClick={() => onRowClick(row)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') onRowClick(row);
                }}
            >
                {inner}
            </Box>
        );
    }
    return <Box style={rowStyle}>{inner}</Box>;
}
